import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkle, X, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const BANNER_ID = 'contract_update_v1';
const STORAGE_KEY = 'contract_update_banner_v1_dismissed';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-shot info banner after the May 2026 contract template revamp.
 *
 * Visibility rules:
 *   • Authenticated users with active subscription/trial.
 *   • NOT shown to users who registered after the cutoff (`dismissed_banners`
 *     is pre-populated server-side with this banner id at registration).
 *   • Dismissal persists on the SERVER (so it doesn't reappear on other
 *     devices/browsers). LocalStorage is just an instant UX cache.
 */
export default function ContractUpdateBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(true);  // hidden until we resolve eligibility client-side

  useEffect(() => {
    if (!user) { setHidden(true); return; }
    // Server says: already dismissed (or pre-dismissed for new accounts) → hide forever.
    const serverDismissed = Array.isArray(user.dismissed_banners)
      && user.dismissed_banners.includes(BANNER_ID);
    if (serverDismissed) {
      setHidden(true);
      return;
    }
    // Fast local check — keeps banner hidden on subsequent reloads even before
    // the server response of /auth/me lands, but server is the source of truth.
    let localDismissed = false;
    try { localDismissed = localStorage.getItem(STORAGE_KEY) === '1'; } catch (_) {}
    setHidden(localDismissed);
  }, [user]);

  const isActive = user?.subscription?.is_active;
  if (!user || !isActive || hidden) return null;

  const handleDismiss = async () => {
    // Optimistic UI
    setHidden(true);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
    // Persist server-side (best-effort, never block UX on failure)
    try {
      await axios.post(`${API}/auth/dismiss-banner`, { banner_id: BANNER_ID }, { withCredentials: true });
    } catch (e) {
      // Banner stays hidden locally; next /auth/me on another device may reshow it,
      // but the user just clicked X here so the local intent is honoured.
    }
  };

  return (
    <div
      className="bg-gradient-to-r from-violet-700 via-violet-600 to-fuchsia-600 text-white px-4 sm:px-6 py-3"
      data-testid="contract-update-banner"
    >
      <div className="max-w-7xl mx-auto flex items-start sm:items-center gap-3">
        <Sparkle size={20} weight="fill" className="text-amber-300 flex-shrink-0 mt-0.5 sm:mt-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <p className="text-sm sm:text-[15px] font-bold leading-snug">
            Abbiamo migliorato la bozza di contratto.
          </p>
          <p className="text-xs sm:text-sm text-violet-100 leading-snug mt-0.5">
            Aggiungi i nuovi dati (IBAN, cancellation policy, foro competente) per averli già pronti sui prossimi contratti.
          </p>
        </div>
        <Link
          to="/properties"
          className="hidden sm:inline-flex items-center gap-1.5 bg-white text-violet-800 hover:bg-violet-50 text-xs font-black uppercase tracking-wider px-4 py-2 transition-colors flex-shrink-0"
          data-testid="contract-update-banner-cta"
          onClick={handleDismiss}
        >
          Vai alle tue case <ArrowRight size={12} weight="bold" />
        </Link>
        <Link
          to="/properties"
          className="sm:hidden inline-flex items-center gap-1 bg-white text-violet-800 text-[11px] font-black uppercase px-3 py-1.5 flex-shrink-0"
          data-testid="contract-update-banner-cta-mobile"
          onClick={handleDismiss}
        >
          Vai <ArrowRight size={11} weight="bold" />
        </Link>
        <button
          onClick={handleDismiss}
          aria-label="Chiudi"
          className="text-white/60 hover:text-white p-1 flex-shrink-0"
          data-testid="contract-update-banner-dismiss"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
